import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle2, LoaderCircle } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { Textarea } from "@/components/ui/textarea";
import { createLead } from "@/lib/api";

const formSchema = z.object({
  full_name: z.string().min(2, "Please enter your full name."),
  organisation: z.string().max(160, "Please keep organisation under 160 characters.").optional().or(z.literal("")),
  email: z.string().email("Enter a valid email address."),
  phone: z.string().regex(/^[0-9+()\-\s]{7,30}$/, "Enter a valid phone number."),
  message: z.string().min(10, "Please share a little more detail.").max(2000, "Please keep your message under 2000 characters."),
});

export const LeadCaptureForm = ({
  enquiryType,
  eyebrow,
  title,
  description,
  submitLabel,
  successTitle,
  successDescription,
  testIdPrefix,
}) => {
  const [latestSubmission, setLatestSubmission] = useState(null);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      full_name: "",
      organisation: "",
      email: "",
      phone: "",
      message: "",
    },
  });

  const onSubmit = async (values) => {
    try {
      const response = await createLead({
        ...values,
        organisation: values.organisation?.trim() || undefined,
        enquiry_type: enquiryType,
      });
      setLatestSubmission(response);
      form.reset();
      toast.success(`${submitLabel} received`, {
        description: "We’ve saved the enquiry and the CareFleet team can now follow up.",
      });
    } catch (error) {
      toast.error("Unable to submit enquiry", {
        description: error?.response?.data?.detail || "Please try again in a moment.",
      });
    }
  };

  return (
    <div className="space-y-8" data-testid={`${testIdPrefix}-form-panel`}>
      <div className="space-y-3">
        <p className="eyebrow">{eyebrow}</p>
        <h2 className="font-heading text-3xl font-semibold text-slate-950" data-testid={`${testIdPrefix}-form-title`}>
          {title}
        </h2>
        <p className="text-base leading-8 text-slate-600" data-testid={`${testIdPrefix}-form-description`}>
          {description}
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" data-testid={`${testIdPrefix}-form`}>
          <div className="grid gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="full_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input {...field} className="h-12 rounded-2xl bg-white" data-testid={`${testIdPrefix}-full-name-input`} />
                  </FormControl>
                  <FormMessage data-testid={`${testIdPrefix}-full-name-error`} />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="organisation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Organisation (optional)</FormLabel>
                  <FormControl>
                    <Input {...field} className="h-12 rounded-2xl bg-white" data-testid={`${testIdPrefix}-organisation-input`} />
                  </FormControl>
                  <FormMessage data-testid={`${testIdPrefix}-organisation-error`} />
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" className="h-12 rounded-2xl bg-white" data-testid={`${testIdPrefix}-email-input`} />
                  </FormControl>
                  <FormMessage data-testid={`${testIdPrefix}-email-error`} />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input {...field} className="h-12 rounded-2xl bg-white" data-testid={`${testIdPrefix}-phone-input`} />
                  </FormControl>
                  <FormMessage data-testid={`${testIdPrefix}-phone-error`} />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Message</FormLabel>
                <FormControl>
                  <Textarea {...field} className="min-h-[150px] rounded-3xl bg-white" data-testid={`${testIdPrefix}-message-input`} />
                </FormControl>
                <FormMessage data-testid={`${testIdPrefix}-message-error`} />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            className="rounded-full bg-brand-navy px-6 py-6 text-white hover:bg-brand-navy/90"
            data-testid={`${testIdPrefix}-submit-button`}
          >
            {form.formState.isSubmitting ? (
              <>
                <LoaderCircle className="h-4 w-4 animate-spin" />
                Sending enquiry
              </>
            ) : (
              submitLabel
            )}
          </Button>
        </form>
      </Form>

      {latestSubmission ? (
        <div className="rounded-[28px] border border-brand-teal/20 bg-brand-teal/10 p-6" data-testid={`${testIdPrefix}-success-message`}>
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-1 h-5 w-5 text-brand-teal" />
            <div>
              <p className="font-heading text-xl font-semibold text-slate-950">{successTitle}</p>
              <p className="mt-2 text-base leading-7 text-slate-700">
                {successDescription} We’ve recorded this as a {latestSubmission.enquiry_type} enquiry.
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};